import { Title } from 'react-head';
import classnames from 'classnames';

import {
    GridColumn,
    GridLayout,
    GridRow,
    HSpacingContainer,
    Link,
    Text,
    Title as TitleMagritte,
    VSpacing,
} from '@hh.ru/magritte-ui';

import { format, formatToReactComponent } from '../../modules/trl';
import { useSelector } from '../../modules/useSelector';
import translation from '../translation';
import { TranslatedComponent } from '../translation/useTranslations';
import RequestIdIcon from './Utils';

import styles from './error-page.less';

const HANDLED_ERROR_CODES = [400, 403, 404] as const;
const DEFAULT_ERROR_CODE = 400;

const TrlKeys = {
    description: {
        404: 'error.page.description.404',
        403: 'error.page.description.403',
        400: 'error.page.description.400',
        default: 'error.page.description.400',
    },

    copyright: 'index.footer.menuitem.copyright',
    logoTitle: 'navi.logo.title',
    pageTitle: {
        404: 'page.404.pageTitle',
        403: 'page.403.pageTitle',
        default: 'page.4xx.pageTitle',
    },
    header: {
        404: 'error.page.title.404',
        403: 'error.page.title.403',
        400: 'error.page.title.400',
        default: 'error.page.title.400',
    },
    errorCode: 'error.page.code',
    indexLink: 'error.page.link.index',
    feedbackLink: 'error.page.link.feedback',
    loginLink: 'error.page.authorization.link',
};

type HandledErrorCodes = (typeof HANDLED_ERROR_CODES)[number];

interface ErrorPageProps {
    code: number;
}

const ErrorPage: TranslatedComponent<ErrorPageProps> = ({ trls, code }) => {
    const potentialyHandledCode = code as HandledErrorCodes;
    const errorCode = HANDLED_ERROR_CODES.includes(potentialyHandledCode) ? potentialyHandledCode : DEFAULT_ERROR_CODE;

    const requestId = useSelector(({ request }) => request.id);
    const localeHost = useSelector(({ locale }) => locale.host) || 'hh.ru';

    const pageTitle = TrlKeys.pageTitle[errorCode as keyof typeof TrlKeys.pageTitle] || TrlKeys.pageTitle.default;
    const pageHeader = TrlKeys.header[errorCode as keyof typeof TrlKeys.header] || TrlKeys.header.default;
    const errorDescription =
        TrlKeys.description[errorCode as keyof typeof TrlKeys.description] || TrlKeys.description.default;

    return (
        <>
            <Title>{trls[pageTitle]}</Title>
            <GridLayout>
                <GridRow>
                    <GridColumn xs={4} s={8} m={12} l={12} flexibleContent>
                        <div className={styles.errorCenteredPage}>
                            <GridColumn xs={4} s={8} m={12} l={12}>
                                <div className={styles.errorContainer}>
                                    <VSpacing default={40} xs={32} />
                                    <div className={styles.errorLogoContainer}>
                                        <Link href={'/'}>
                                            <span
                                                className={classnames(
                                                    `${styles.supernovaLogo} ${styles.supernovaLogoHhRu}`,
                                                    {
                                                        [styles.supernovaLogoRabotaBy]: localeHost === 'rabota.by',
                                                        [styles.supernovaLogoHrZarplataRu]:
                                                            localeHost === 'hr.zarplata.ru',
                                                    }
                                                )}
                                            />
                                        </Link>
                                    </div>
                                    <VSpacing default={40} />
                                    <VSpacing default={20} xs={0} />
                                    <div className={styles.errorContentWrapper}>
                                        <div className={styles.errorContent}>
                                            <div className={styles.errorTextContent}>
                                                <VSpacing default={16} xs={0} />
                                                <TitleMagritte Element="h1" size="extra-large">
                                                    {trls[pageHeader]}
                                                </TitleMagritte>
                                                <VSpacing default={8} />
                                                <Text typography="label-3-regular" style="secondary">
                                                    {format(trls[TrlKeys.errorCode], {
                                                        '{0}': errorCode || '',
                                                    })}
                                                </Text>
                                                <VSpacing default={16} />

                                                <div className={styles.errorText}>
                                                    {formatToReactComponent(trls[errorDescription], {
                                                        '{0}': <RequestIdIcon requestId={requestId || ''} />,
                                                    })}
                                                </div>

                                                <VSpacing default={24} />
                                                <HSpacingContainer default={24}>
                                                    {errorCode === 403 ? (
                                                        <Link
                                                            typography="label-3-regular"
                                                            href={`https://${localeHost}/account/login`}
                                                        >
                                                            {trls[TrlKeys.loginLink]}
                                                        </Link>
                                                    ) : (
                                                        <Link typography="label-3-regular" href={'/'}>
                                                            {trls[TrlKeys.indexLink]}
                                                        </Link>
                                                    )}
                                                    <Link
                                                        typography="label-3-regular"
                                                        href={'https://feedback.hh.ru/ticket/add'}
                                                        target="_blank"
                                                    >
                                                        {trls[TrlKeys.feedbackLink]}
                                                    </Link>
                                                </HSpacingContainer>
                                            </div>
                                            <div
                                                className={classnames([styles.errorPicture], {
                                                    [styles.errorPictureBadRequest]: errorCode === 400,
                                                    [styles.errorPictureForbidden]: errorCode === 403,
                                                    [styles.errorPictureNotFound]: errorCode === 404,
                                                })}
                                            />
                                        </div>
                                    </div>
                                    <VSpacing default={24} />
                                    <Text typography="label-3-regular" style="tertiary">
                                        {format(trls[TrlKeys.copyright], {
                                            '{0}': new Date().getFullYear(),
                                        })}
                                    </Text>
                                    <VSpacing default={40} xs={32} />
                                </div>
                            </GridColumn>
                        </div>
                    </GridColumn>
                </GridRow>
            </GridLayout>
        </>
    );
};

export default translation(ErrorPage);
